// Client-side API layer for the household mapping page for Slant (APP-1692).
// Base `slant/household-mappings` (firm/auth-scoped):
//
//   useHouseholdMappingReview  GET  review?group&filter&slantRead&includeArchived
//   useReviewSearch            GET  review/search?query&(slantHouseholdId|householdId)
//   useSaveReview              POST review        (queues one Save job)
//   useReviewSaveStatus        GET  review/save?jobId  (a Save job; owner decision 7)
//
// The page holds the advisor's changes in React state; nothing is written
// until the POST. The POST queues one job, and the worker writes it. Only the first GET of the page (`slantRead=cached`) and
// "Compare again" (`slantRead=again`) can make the api read Slant. Each other
// GET sends `slantRead=kept`, and the search and the POST send no Slant
// request at all.
import { useAuth0 } from "@auth0/auth0-react";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import type {
  HouseholdMappingReview,
  ReviewGroup,
  ReviewPair,
  ReviewSave,
  ReviewSaveQueued,
  ReviewSaveStatus,
  SlantRead,
} from "../../../../../api/src/slant/household-mapping-review.service";
import { ApiError, authenticatedFetch, serverError } from "../../../lib/api";

export type {
  ConnectedRow,
  HouseholdMappingReview,
  ReviewGroup,
  ReviewHousehold,
  ReviewPair,
  ReviewRow,
  ReviewSave,
  ReviewSaveResult,
  ReviewSaveStatus,
  ReviewSlantHousehold,
} from "../../../../../api/src/slant/household-mapping-review.service";

type Wrapped<T> = { data: T };

const BASE = "/slant/household-mappings/review";

/** The rows a list shows at most, as the api's `REVIEW_ROW_LIMIT` (R15). */
export const REVIEW_ROW_LIMIT = 50;

/** The start of the query key of each read of the review list. */
export const REVIEW_QUERY_KEY = "slant-household-mapping-review";

/** The query key of the status of the latest Save job of the firm. */
export const SAVE_STATUS_QUERY_KEY = "slant-household-mapping-review-save";

/** How often the page reads the status while a Save job waits or runs, in ms. */
export const SAVE_STATUS_POLL_MS = 4000;

/** Whether a Save job of the firm is waiting or running. */
export function isSaveRunning(status: ReviewSaveStatus | undefined): boolean {
  return status?.state === "waiting" || status?.state === "active";
}

/**
 * The `message` of the api's error body for a failed request, or `fallback`
 * when the error carries none. On HTTP 429 that is the api's rate-limit
 * message, which the page shows as it is.
 */
export function apiMessage(error: unknown, fallback: string): string {
  // `serverError` casts the parsed body, so `message` is checked, not trusted.
  const message: unknown = serverError(error)?.body?.message;
  return typeof message === "string" && message.trim() !== ""
    ? message
    : fallback;
}

/**
 * Read the review list. The first read sends `slantRead=cached`;
 * `compareAgain` sends `slantRead=again`; each other read sends
 * `slantRead=kept`, which makes no Slant request.
 */
export function useHouseholdMappingReview(params: {
  group: ReviewGroup;
  filter: string;
  /** "Show archived households" (R105). */
  includeArchived: boolean;
}) {
  const { getAccessTokenSilently } = useAuth0();
  const nextRead = useRef<SlantRead>("cached");
  const [isComparingAgain, setIsComparingAgain] = useState(false);
  const [compareFailure, setCompareFailure] = useState<{
    error: unknown;
  } | null>(null);

  // queryFn closes over getAccessTokenSilently, which is not referentially
  // stable, so it cannot be in the key (see `useAuthenticatedQuery`,
  // `lib/api.ts`).
  // eslint-disable-next-line @tanstack/query/exhaustive-deps
  const query = useQuery({
    queryKey: [
      REVIEW_QUERY_KEY,
      params.group,
      params.filter,
      params.includeArchived,
    ],
    queryFn: async () => {
      const slantRead = nextRead.current;
      nextRead.current = "kept";
      const search = new URLSearchParams({
        group: params.group,
        slantRead,
      });
      if (params.filter !== "") search.set("filter", params.filter);
      if (params.includeArchived) search.set("includeArchived", "true");
      try {
        return await authenticatedFetch<Wrapped<HouseholdMappingReview>>(
          `${process.env.REACT_APP_API_ROOT}${BASE}?${search.toString()}`,
          getAccessTokenSilently,
        )();
      } catch (error) {
        // The page did not open: the next read is the opening read again.
        if (slantRead === "cached") nextRead.current = "cached";
        throw error;
      }
    },
    placeholderData: keepPreviousData,
    // A 429 tells the advisor to wait, so it is not retried.
    retry: (failureCount, error) =>
      !(error instanceof ApiError && error.status === 429) && failureCount < 3,
  });

  // The failure belongs to the list it was shown for.
  const readKey = `${params.group}\n${params.filter}\n${params.includeArchived}`;
  useEffect(() => setCompareFailure(null), [readKey]);

  const { refetch } = query;
  const compareAgain = useCallback(async () => {
    nextRead.current = "again";
    setIsComparingAgain(true);
    try {
      const result = await refetch();
      // After the last try. When Slant answers 429 and the api has a kept
      // list, the api answers 200 with `slantReadMessage`, which is not a
      // failure. With no kept list the api answers 429, and that is a failure.
      setCompareFailure(result.isError ? { error: result.error } : null);
    } finally {
      nextRead.current = "kept";
      setIsComparingAgain(false);
    }
  }, [refetch]);

  return {
    data: query.data,
    error: query.error,
    isPending: query.isPending,
    isError: query.isError,
    compareAgain,
    isComparingAgain,
    /** The failed "Compare again", while the rows of the last read stay. */
    compareFailure,
  };
}

/** Which record a search starts from; the search finds records of the other kind. */
export type SearchSide = { slantHouseholdId: string } | { householdId: number };

/** Search for a record to pair with `side`. Returns the function that searches. */
export function useReviewSearch(side: SearchSide) {
  const { getAccessTokenSilently } = useAuth0();
  return useMutation({
    mutationFn: async (query: string) => {
      const search = new URLSearchParams({ query });
      if ("slantHouseholdId" in side) {
        search.set("slantHouseholdId", side.slantHouseholdId);
      } else {
        search.set("householdId", String(side.householdId));
      }
      const response = await authenticatedFetch<
        Wrapped<{ results: ReviewPair[] }>
      >(
        `${process.env.REACT_APP_API_ROOT}${BASE}/search?${search.toString()}`,
        getAccessTokenSilently,
      )();
      return response.data.results;
    },
  });
}

/**
 * Save the changes: one POST that queues one Save job, and answers with its
 * id. The page then reads the job with {@link useReviewSaveStatus}.
 */
export function useSaveReview() {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (changes: ReviewSave): Promise<ReviewSaveQueued> => {
      const { data } = await authenticatedFetch<Wrapped<ReviewSaveQueued>>(
        `${process.env.REACT_APP_API_ROOT}${BASE}`,
        getAccessTokenSilently,
        { method: "POST", body: changes },
      )();
      return data;
    },
    // Also after a refusal: a 409 means a Save job of the firm is running.
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: [SAVE_STATUS_QUERY_KEY] }),
  });
}

/**
 * The status of a Save job (owner decision 7). With `jobId`, the id the POST
 * of this tab answered: that job and its result. Without it, only a waiting
 * or active job of the firm, or the user's own job that failed in the last
 * hour. While the job waits or runs, the page reads it again every `pollMs`.
 */
export function useReviewSaveStatus(pollMs: number, jobId: string | null) {
  const { getAccessTokenSilently } = useAuth0();

  // eslint-disable-next-line @tanstack/query/exhaustive-deps
  return useQuery({
    queryKey: [SAVE_STATUS_QUERY_KEY, jobId],
    queryFn: async () => {
      const search =
        jobId === null ? "" : `?${new URLSearchParams({ jobId }).toString()}`;
      const { data } = await authenticatedFetch<Wrapped<ReviewSaveStatus>>(
        `${process.env.REACT_APP_API_ROOT}${BASE}/save${search}`,
        getAccessTokenSilently,
      )();
      return data;
    },
    refetchInterval: (query) =>
      isSaveRunning(query.state.data) ? pollMs : false,
  });
}
